import React, { useContext, useEffect, useState } from 'react';
import { Button, FormControl, Table } from 'react-bootstrap';
import PresellRow from './PresellRow';
import axios from 'axios';
import AuthContext from '../../authentication/AuthContext';
import { useAlert } from 'react-alert';
import UserContext from '../../user/UserContext';
import { handlePresellError } from './AlertUtils';
import LoadingSpinner from '../../common/LoadingSpinner';
import moment from 'moment';
import { IconWrapper } from '../../common/IconWrapper';
import { faRedo } from '@fortawesome/free-solid-svg-icons';


export default function Presells(props) {
    const anyOrderDeadlineStatus = 'All Order Deadlines';
    const orderingDeadlineInFuture = 'Ordering Open';
    const orderingDeadlinePassed = 'Ordering Closed';
    const presellStatusOptions = [anyOrderDeadlineStatus, orderingDeadlineInFuture, orderingDeadlinePassed];

    const allowedStatuses = !!props.allowedStatuses ? [...props.allowedStatuses] : [];
    const excludedStatuses = !!props.excludedStatuses ? [...props.excludedStatuses] : [];

    const anyOrderHeaderStatus = 'Any Order Status';

    const [loading, setLoading] = useState(true);
    const { userDetails } = useContext(UserContext);
    const [presells, setPresells] = useState([]);
    const [nameFilter, setNameFilter] = useState('');
    const [presellStatusFilter, setPresellStatusFilter] = useState(anyOrderDeadlineStatus);
    const [orderStatusFilter, setOrderStatusFilter] = useState(anyOrderHeaderStatus);
    const [allOrderStatuses, setAllOrderStatuses] = useState(['Loading...']);

    let cas = useContext(AuthContext);
    let alert = useAlert();

    function shouldDisplayStatus(status) {
        if (allowedStatuses.length) {
            return allowedStatuses.includes(status);
        }

        return !excludedStatuses.includes(status);
    }

    const getActivePresells = () => {
        axios
            .get(window.CONFIG['ws.activoc-presell-public.presells.active'].replace('{filterType}', props.filterType), {
                headers: {
                    Authorization: cas,
                    'Content-Type': 'application/json',
                    'x-franchise-code': userDetails.currentFranchise.code
                }
            })
            .then(response => {
                const presells = response.data;
                const allStatuses = [anyOrderHeaderStatus,
                    ...new Set(presells.map(p => !!p.orderStatus ? p.orderStatus : 'NEW')
                        .filter(shouldDisplayStatus))];
                setAllOrderStatuses(allStatuses);
                setPresells(presells);
                setLoading(false);

            })
            .catch(error => {
                handlePresellError(alert, error, 'Could not fetch ' + props.filterType + '.');
                setLoading(false);
            });
    };

    useEffect(() => {
        if (userDetails && userDetails.currentFranchise) {
            getActivePresells();

        } else {
            setPresells([]);
            setLoading(false);
        }
    }, [cas, userDetails]);

    function presellDateMatchesFilter(presell, filter) {
        const today = moment();
        const endDate = moment(presell.endDate, 'YYYY-MM-DD');
        const orderStatus = endDate.isBefore(today) ? orderingDeadlinePassed : orderingDeadlineInFuture;
        return orderStatus === filter;
    }

    function orderStatusMatchesFilter(presell, filter) {
        if (filter === 'NEW') {
            return presell.orderStatus === null;
        } else if (filter === anyOrderHeaderStatus) {
            return shouldDisplayStatus(presell.orderStatus);
        }

        return presell.orderStatus === filter;
    }

    function filteredPresells(allPresells, nameFilter, presellStatusFilter, orderStatusFilter) {
        let filtered = allPresells;
        filtered = filtered.filter(p => !nameFilter || p.name.toUpperCase().includes(nameFilter.toUpperCase()));
        filtered = filtered.filter(p => presellStatusFilter === anyOrderDeadlineStatus || presellDateMatchesFilter(p, presellStatusFilter));
        filtered = filtered.filter(p => orderStatusMatchesFilter(p, orderStatusFilter));
        return filtered;
    }

    return (
        <div>
            <div className={'page-header'}>
                <div className={'content'}>
                    <div className={'page-header-container'}>
                        <div>
                            <h1>{props.title} System</h1>
                        </div>
                    </div>
                </div>
            </div>
            <div className={'content'}>
                <div className={'presell-filters-container'}>
                    <FormControl
                        type={'text'}
                        value={nameFilter}
                        onChange={e => setNameFilter(e.target.value)}
                        placeholder={`Filter by ${props.title} Name`}
                        size={'lg'}
                        className={'search-term'}
                        id={'presell-name-filter'}
                        data-testid={'presell-name-filter'}
                    />
                    <FormControl
                        as={'select'}
                        value={presellStatusFilter}
                        onChange={event => setPresellStatusFilter(event.target.value)}
                        size={'lg'}
                        id={'presell-status-filter-select'}
                        data-testid={'presell-status-filter-select'}
                    >
                        {presellStatusOptions.map(s => <option value={s} key={s}>{s}</option>)}
                    </FormControl>
                    <FormControl
                        as={'select'}
                        value={orderStatusFilter}
                        onChange={event => setOrderStatusFilter(event.target.value)}
                        size={'lg'}
                        id={'presell-order-status-filter-select'}
                        data-testid={'presell-order-status-filter-select'}
                    >
                        {allOrderStatuses.map(s => <option value={s} key={s}>{s}</option>)}
                    </FormControl>
                    <Button
                        variant={'primary'}
                        onClick={() => {
                            setNameFilter('');
                            setPresellStatusFilter(anyOrderDeadlineStatus);
                            setOrderStatusFilter(anyOrderHeaderStatus);
                        }}
                        size={'lg'}
                        data-testid={'clear-search'}
                        title={'Reset'}
                    >
                        <IconWrapper icon={faRedo} flip={'horizontal'} />
                    </Button>
                </div>
                <Table id='presells-table' className='presells-tables' hover={true}>
                    <thead id='presells-table-headers'>
                    <tr>
                        <th className={'th-col-name'}>
                            <p>Name</p>
                        </th>
                        <th className={'th-col-timer'}>
                            <p>Time Left to Order</p>
                        </th>
                        {/*<th className={'th-col-notifications-header'}>Notifications</th>*/}
                        <th className={'th-col-lines'}>
                            <p>No. of Lines</p>
                        </th>
                        <th className={'th-col-deals'}>
                            <p>No. of Deals</p>
                        </th>
                        <th className={'th-col-status'}>
                            <p>Order Status</p>
                        </th>
                        <th className={'th-col-cases'}>
                            <p>Cases Ordered</p>
                        </th>
                        <th className={'th-col-total'}>
                            <p>Total</p>
                        </th>
                    </tr>
                    </thead>
                    <tbody id={'presells-table-body'} data-testid={'presells-table-body'}>
                    {loading ? (
                        <tr>
                            <td colSpan={9}>
                                <LoadingSpinner />
                            </td>
                        </tr>
                    ) : (
                        filteredPresells(presells, nameFilter, presellStatusFilter, orderStatusFilter).map(presell => (
                            <PresellRow key={presell.id} presell={presell} />
                        ))
                    )}
                    </tbody>
                </Table>
            </div>
        </div>
    );
}

